import { FileText } from "lucide-react";

/**
 * The uploaded evidence behind an application, and how a committee screen
 * shows it.
 *
 * A recommendation made without sight of the certificate is a guess, so every
 * scan an applicant attached has to be openable from the review screen — and
 * the things that are NOT files (O'level subjects, stored as a map) have to
 * read as results rather than as "[object Object]", which is what happens the
 * moment anything assumes that map is an array.
 *
 * Shared by both committee review screens so the entry side and the upgrade
 * side cannot drift into showing the same evidence two different ways.
 */

const STORAGE_BASE = "https://api.niqsng.org/storage";

/** Passes absolute URLs through; strips a leading slash so paths don't double up. */
export const storageUrl = (p?: string | null): string | null =>
  !p ? null : String(p).startsWith("http") ? String(p) : `${STORAGE_BASE}/${String(p).replace(/^\//, "")}`;

/** The uploaded certificate arrives under several different keys. */
export const certOf = (row: any): string | null =>
  row?.cert_file ?? row?.certificate_path ?? row?.cert_url ?? row?.file ?? null;

/** A link to any uploaded document, or a dash when nothing was attached. */
export function FileLink({ path }: { path?: string | null }) {
  const href = storageUrl(path);

  if (!href) return <span className="text-gray-400">—</span>;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-indigo-600 font-semibold hover:underline"
    >
      View
    </a>
  );
}

/**
 * O'level subjects as one line of text, for a table cell.
 *
 * They are stored as a `{ subject: grade }` MAP, not a list — so anything that
 * assumes an array renders the object itself and tells the reviewer nothing.
 * Both shapes are handled because the wizard and the older imports disagree.
 */
export function subjectsText(subjects: any): string {
  if (!subjects || typeof subjects !== "object") return "—";

  const entries = Array.isArray(subjects)
    ? subjects.map((s: any) =>
        typeof s === "string" ? s : `${s?.subject ?? s?.name ?? "—"}: ${s?.grade ?? "—"}`,
      )
    : Object.entries(subjects).map(([k, v]) => `${k}: ${String(v)}`);

  return entries.length ? entries.join(", ") : "—";
}

/**
 * The same subjects as one chip each — grades read at a glance instead of as a
 * run-on string, which is how a reviewer checks them against the slip.
 */
export function SubjectGrades({ subjects }: { subjects: any }) {
  if (!subjects || typeof subjects !== "object") {
    return <span className="text-gray-300">—</span>;
  }

  const entries: [string, any][] = Array.isArray(subjects)
    ? subjects.map((s: any) =>
        typeof s === "string"
          ? [s, ""]
          : [String(s?.subject ?? s?.name ?? "—"), s?.grade ?? ""],
      )
    : Object.entries(subjects);

  if (!entries.length) return <span className="text-gray-300">—</span>;

  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([subject, grade], i) => (
        <span
          key={`${subject}-${i}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-200 bg-white text-[11px]"
        >
          <span className="text-gray-600">{subject}</span>
          {grade === "" ? null : <span className="font-bold text-gray-900">{String(grade)}</span>}
        </span>
      ))}
    </div>
  );
}

/**
 * A scanned document — a result slip, a degree certificate — shown rather than
 * hidden behind a link.
 *
 * A reviewer checking grades against the slip, or a qualification against the
 * certificate, should not have to open a new tab for every row. A PDF cannot be
 * thumbnailed, so that one gets a link — and nothing at all is called out in
 * red, because these uploads are compulsory and an absence is a finding rather
 * than an empty cell.
 */
export function ScanPreview({
  path,
  alt = "Scanned document",
}: {
  path?: string | null;
  alt?: string;
}) {
  const url = storageUrl(path);

  if (!url) {
    return <span className="text-xs font-semibold text-rose-600">Not uploaded</span>;
  }

  if (/\.pdf(\?|$)/i.test(url)) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-indigo-600 hover:underline text-sm"
      >
        <FileText className="w-4 h-4" /> Open PDF
      </a>
    );
  }

  return (
    <a href={url} target="_blank" rel="noreferrer" title="Open full size">
      <img
        src={url}
        alt={alt}
        className="w-40 h-28 object-cover rounded-lg border hover:opacity-90 transition-opacity"
      />
    </a>
  );
}
